import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Alert,
  CircularProgress,
  Tooltip,
  Collapse,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CheckCircle as CheckCircleIcon,
  Build as BuildIcon,
  LocationOn as LocationOnIcon,
  Person as PersonIcon,
  AccessTime as AccessTimeIcon,
  Photo as PhotoIcon,
} from '@mui/icons-material';

const API_BASE_URL = process.env.REACT_APP_API_URL?.replace(/\/$/, '') || 'https://ventro.energy';

const DefectReports = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedRow, setExpandedRow] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [statistics, setStatistics] = useState(null);
  const [selectedReport, setSelectedReport] = useState(null);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [updating, setUpdating] = useState(false);

  const fetchReports = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('access_token');
      const params = new URLSearchParams();

      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (priorityFilter !== 'all') params.append('priority', priorityFilter);
      if (sourceFilter !== 'all') params.append('source_type', sourceFilter);

      const response = await axios.get(
        `${API_BASE_URL}/api/defect-reports/?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const data = response.data.results || response.data || [];
      setReports(data);
    } catch (err) {
      console.error('Error fetching defect reports:', err);
      setError('Failed to load defect reports');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, priorityFilter, sourceFilter]);

  const fetchStatistics = useCallback(async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API_BASE_URL}/api/defect-reports/statistics/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStatistics(response.data);
    } catch (err) {
      console.error('Error fetching statistics:', err);
    }
  }, []);

  useEffect(() => {
    fetchReports();
    fetchStatistics();
  }, [fetchReports, fetchStatistics]);

  const handleStatusChange = async (reportId, newStatus, notes = '') => {
    setUpdating(true);
    try {
      const token = localStorage.getItem('access_token');
      let endpoint = '';

      switch (newStatus) {
        case 'acknowledged':
          endpoint = `${API_BASE_URL}/api/defect-reports/${reportId}/acknowledge/`;
          break;
        case 'in_repair':
          endpoint = `${API_BASE_URL}/api/defect-reports/${reportId}/start_repair/`;
          break;
        case 'resolved':
          endpoint = `${API_BASE_URL}/api/defect-reports/${reportId}/resolve/`;
          break;
        case 'rejected':
          endpoint = `${API_BASE_URL}/api/defect-reports/${reportId}/reject/`;
          break;
        default:
          throw new Error('Invalid status');
      }

      await axios.post(
        endpoint,
        { resolution_notes: notes, rejection_reason: notes },
        { headers: { Authorization: `Bearer ${token}` }, withCredentials: true }
      );

      fetchReports();
      fetchStatistics();
      setStatusDialogOpen(false);
      setSelectedReport(null);
      setResolutionNotes('');
    } catch (err) {
      console.error('Error updating status:', err);
      setError('Failed to update status');
    } finally {
      setUpdating(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      reported: { bg: '#fef3c7', color: '#92400e', border: '#fcd34d' },
      acknowledged: { bg: '#e0e7ff', color: '#3730a3', border: '#a5b4fc' },
      in_repair: { bg: '#dbeafe', color: '#1e40af', border: '#93c5fd' },
      resolved: { bg: '#dcfce7', color: '#166534', border: '#86efac' },
      rejected: { bg: '#fee2e2', color: '#991b1b', border: '#fca5a5' },
    };
    const style = styles[status] || { bg: '#f3f4f6', color: '#475569', border: '#d1d5db' };

    const labels = {
      reported: 'Reported',
      acknowledged: 'Acknowledged',
      in_repair: 'In Repair',
      resolved: 'Resolved',
      rejected: 'Rejected',
    };

    return (
      <span style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '2px 10px',
        fontSize: '12px',
        fontWeight: 500,
        borderRadius: '9999px',
        backgroundColor: style.bg,
        color: style.color,
        border: `1px solid ${style.border}`,
      }}>
        {labels[status] || status}
      </span>
    );
  };

  const getPriorityBadge = (priority) => {
    const styles = {
      low: { bg: '#f3f4f6', color: '#475569' },
      medium: { bg: '#fef3c7', color: '#92400e' },
      high: { bg: '#fed7aa', color: '#c2410c' },
      critical: { bg: '#fee2e2', color: '#991b1b' },
    };
    const style = styles[priority] || styles.medium;

    const labels = {
      low: 'Low',
      medium: 'Medium',
      high: 'High',
      critical: 'Critical',
    };

    return (
      <span style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '2px 8px',
        fontSize: '11px',
        fontWeight: 500,
        borderRadius: '4px',
        backgroundColor: style.bg,
        color: style.color,
      }}>
        {labels[priority] || priority}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const filteredReports = reports.filter((report) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      (report.equipment_name?.toLowerCase().includes(searchLower) || false) ||
      (report.description?.toLowerCase().includes(searchLower) || false) ||
      (report.reporter_name?.toLowerCase().includes(searchLower) || false) ||
      (report.defect_type_display?.toLowerCase().includes(searchLower) || false)
    );
  });

  const openStatusDialog = (report, targetStatus) => {
    setSelectedReport({ ...report, targetStatus });
    setStatusDialogOpen(true);
    setResolutionNotes('');
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#F5F5F5' }}>
      {/* Header */}
      <Box sx={{
        background: 'linear-gradient(to right, #FEE2E2, #F5F5F5)',
        borderBottom: '1px solid #D9D9D9',
        px: 3,
        py: 2,
        mb: 3
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', maxWidth: 1400, margin: '0 auto' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{
              p: 1,
              bgcolor: 'white',
              borderRadius: '12px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              border: '1px solid #D9D9D9',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <BuildIcon sx={{ fontSize: 24, color: '#FE3F06' }} />
            </Box>
            <div>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b' }}>
                Defect Reports
              </Typography>
              <Typography variant="body2" sx={{ color: '#64748b', fontSize: 13 }}>
                Manage and track equipment defect reports
              </Typography>
            </div>
          </Box>
          <Button
            variant="outlined"
            size="small"
            startIcon={<RefreshIcon />}
            onClick={() => { fetchReports(); fetchStatistics(); }}
            sx={{
              borderColor: '#D9D9D9',
              color: '#475569',
              borderRadius: '12px',
              '&:hover': { borderColor: '#FE3F06', bgcolor: '#FEF2F2' }
            }}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      <Box sx={{ px: 3, maxWidth: 1400, margin: '0 auto' }}>

      {/* Statistics */}
      {statistics && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[
            { label: 'Total', value: statistics.total || 0, color: '#FE3F06' },
            { label: 'Pending', value: statistics.by_status?.reported || 0, color: '#f59e0b' },
            { label: 'In Repair', value: statistics.by_status?.in_repair || 0, color: '#3b82f6' },
            { label: 'Resolved', value: statistics.by_status?.resolved || 0, color: '#22c55e' },
            { label: 'Critical', value: statistics.critical_unresolved || 0, color: '#ef4444' },
            { label: 'Last 7 Days', value: statistics.recent_7_days || 0, color: '#8b5cf6' },
          ].map((stat, idx) => (
            <Grid item xs={6} sm={4} md={2} key={idx}>
              <Paper sx={{
                p: 2,
                textAlign: 'center',
                border: '2px solid #D9D9D9',
                boxShadow: 'none',
                borderRadius: '12px',
                transition: 'all 0.2s',
                '&:hover': { borderColor: stat.color, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }
              }}>
                <Typography variant="h5" sx={{ fontWeight: 700, color: stat.color }}>
                  {stat.value}
                </Typography>
                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 500 }}>
                  {stat.label}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3, border: '1px solid #D9D9D9', boxShadow: 'none', borderRadius: '12px' }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search reports..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#94a3b8', fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                  '& fieldset': { borderColor: '#D9D9D9' },
                  '&:hover fieldset': { borderColor: '#FE3F06' },
                  '&.Mui-focused fieldset': { borderColor: '#FE3F06' },
                }
              }}
            />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value)}
                sx={{ borderRadius: '12px', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#D9D9D9' }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#FE3F06' } }}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="reported">Reported</MenuItem>
                <MenuItem value="acknowledged">Acknowledged</MenuItem>
                <MenuItem value="in_repair">In Repair</MenuItem>
                <MenuItem value="resolved">Resolved</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Priority</InputLabel>
              <Select
                value={priorityFilter}
                label="Priority"
                onChange={(e) => setPriorityFilter(e.target.value)}
                sx={{ borderRadius: '12px', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#D9D9D9' }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#FE3F06' } }}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="low">Low</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="high">High</MenuItem>
                <MenuItem value="critical">Critical</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Source</InputLabel>
              <Select
                value={sourceFilter}
                label="Source"
                onChange={(e) => setSourceFilter(e.target.value)}
                sx={{ borderRadius: '12px', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#D9D9D9' }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#FE3F06' } }}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="equipment">Equipment</MenuItem>
                <MenuItem value="warehouse">Warehouse</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Loading */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
          <CircularProgress size={32} sx={{ color: '#64748b' }} />
        </Box>
      ) : (
        /* Table */
        <TableContainer component={Paper} sx={{ border: '1px solid #D9D9D9', boxShadow: 'none', borderRadius: '12px', overflow: 'hidden' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#E5E5E5', borderBottom: '2px solid #FE3F06' }}>
                <TableCell width={40}></TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#1e293b', fontSize: 13 }}>Equipment</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#1e293b', fontSize: 13 }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#1e293b', fontSize: 13 }}>Priority</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#1e293b', fontSize: 13 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#1e293b', fontSize: 13 }}>Reporter</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#1e293b', fontSize: 13 }}>Date</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600, color: '#1e293b', fontSize: 13 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredReports.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 6, color: '#64748b' }}>
                    No defect reports found
                  </TableCell>
                </TableRow>
              ) : (
                filteredReports.map((report) => (
                  <React.Fragment key={report.id}>
                    <TableRow
                      hover
                      sx={{
                        cursor: 'pointer',
                        transition: 'background-color 0.2s',
                        '&:hover': { bgcolor: '#FEF2F2' },
                      }}
                      onClick={() => setExpandedRow(expandedRow === report.id ? null : report.id)}
                    >
                      <TableCell>
                        <IconButton size="small">
                          {expandedRow === report.id ? (
                            <ExpandLessIcon sx={{ fontSize: 18, color: '#64748b' }} />
                          ) : (
                            <ExpandMoreIcon sx={{ fontSize: 18, color: '#64748b' }} />
                          )}
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500, color: '#1e293b' }}>
                          {report.equipment_name || 'Unknown'}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#64748b' }}>
                          {report.source_type_display}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ color: '#475569', fontSize: 13 }}>
                        {report.defect_type_display}
                      </TableCell>
                      <TableCell>{getPriorityBadge(report.priority)}</TableCell>
                      <TableCell>{getStatusBadge(report.status)}</TableCell>
                      <TableCell sx={{ color: '#475569', fontSize: 13 }}>
                        {report.reporter_name || '-'}
                      </TableCell>
                      <TableCell sx={{ color: '#64748b', fontSize: 12 }}>
                        {formatDate(report.reported_at)}
                      </TableCell>
                      <TableCell align="center">
                        {report.status === 'reported' && (
                          <Tooltip title="Acknowledge">
                            <IconButton
                              size="small"
                              onClick={(e) => { e.stopPropagation(); handleStatusChange(report.id, 'acknowledged'); }}
                              sx={{ color: '#64748b', '&:hover': { color: '#475569' } }}
                            >
                              <CheckCircleIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                        {['reported', 'acknowledged'].includes(report.status) && (
                          <Tooltip title="Start Repair">
                            <IconButton
                              size="small"
                              onClick={(e) => { e.stopPropagation(); handleStatusChange(report.id, 'in_repair'); }}
                              sx={{ color: '#64748b', '&:hover': { color: '#475569' } }}
                            >
                              <BuildIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                        {!['resolved', 'rejected'].includes(report.status) && (
                          <Tooltip title="Resolve">
                            <IconButton
                              size="small"
                              onClick={(e) => { e.stopPropagation(); openStatusDialog(report, 'resolved'); }}
                              sx={{ color: '#64748b', '&:hover': { color: '#166534' } }}
                            >
                              <CheckCircleIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>

                    {/* Expanded */}
                    <TableRow>
                      <TableCell colSpan={8} sx={{ p: 0, borderBottom: expandedRow === report.id ? '1px solid #D9D9D9' : 'none' }}>
                        <Collapse in={expandedRow === report.id}>
                          <Box sx={{ p: 3, bgcolor: '#F5F5F5' }}>
                            <Grid container spacing={3}>
                              <Grid item xs={12} md={6}>
                                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                  Description
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#475569', mt: 1 }}>
                                  {report.description || 'No description'}
                                </Typography>

                                {report.location_name && (
                                  <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <LocationOnIcon sx={{ fontSize: 16, color: '#64748b' }} />
                                    <Typography variant="body2" sx={{ color: '#64748b' }}>
                                      {report.location_name}
                                    </Typography>
                                  </Box>
                                )}

                                {report.resolution_notes && (
                                  <Box sx={{ mt: 2 }}>
                                    <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>
                                      Resolution Notes
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: '#475569', mt: 0.5 }}>
                                      {report.resolution_notes}
                                    </Typography>
                                  </Box>
                                )}
                              </Grid>

                              <Grid item xs={12} md={6}>
                                {report.photos && report.photos.length > 0 && (
                                  <Box sx={{ mb: 2 }}>
                                    <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      <PhotoIcon sx={{ fontSize: 14 }} />
                                      Photos ({report.photos.length})
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                                      {report.photos.map((photo) => (
                                        <Box
                                          key={photo.id}
                                          component="img"
                                          src={photo.photo_url || photo.photo}
                                          alt="Defect"
                                          sx={{
                                            width: 80,
                                            height: 80,
                                            objectFit: 'cover',
                                            borderRadius: '6px',
                                            cursor: 'pointer',
                                            border: '1px solid #e5e7eb',
                                          }}
                                          onClick={() => window.open(photo.photo_url || photo.photo, '_blank')}
                                        />
                                      ))}
                                    </Box>
                                  </Box>
                                )}

                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <PersonIcon sx={{ fontSize: 16, color: '#64748b' }} />
                                    <Typography variant="body2" sx={{ color: '#64748b' }}>
                                      {report.reporter_name || 'Unknown'}
                                    </Typography>
                                  </Box>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <AccessTimeIcon sx={{ fontSize: 16, color: '#64748b' }} />
                                    <Typography variant="body2" sx={{ color: '#64748b' }}>
                                      {formatDate(report.reported_at)}
                                    </Typography>
                                  </Box>
                                  {report.resolved_at && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <CheckCircleIcon sx={{ fontSize: 16, color: '#166534' }} />
                                      <Typography variant="body2" sx={{ color: '#64748b' }}>
                                        Resolved: {formatDate(report.resolved_at)}
                                      </Typography>
                                    </Box>
                                  )}
                                </Box>
                              </Grid>
                            </Grid>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Dialog */}
      <Dialog
        open={statusDialogOpen}
        onClose={() => setStatusDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: '16px' } }}
      >
        <DialogTitle sx={{
          fontWeight: 600,
          color: '#1e293b',
          background: 'linear-gradient(to right, #FEE2E2, #F5F5F5)',
          borderBottom: '1px solid #D9D9D9'
        }}>
          {selectedReport?.targetStatus === 'resolved' ? 'Resolve Report' : 'Update Report'}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Typography variant="body2" sx={{ mb: 2, color: '#64748b' }}>
            Add notes for this status change.
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Notes"
            value={resolutionNotes}
            onChange={(e) => setResolutionNotes(e.target.value)}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px',
                '& fieldset': { borderColor: '#D9D9D9' },
                '&:hover fieldset': { borderColor: '#FE3F06' },
                '&.Mui-focused fieldset': { borderColor: '#FE3F06' },
              }
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid #D9D9D9', bgcolor: '#F5F5F5' }}>
          <Button
            onClick={() => setStatusDialogOpen(false)}
            sx={{ color: '#64748b', borderRadius: '12px' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => handleStatusChange(selectedReport?.id, selectedReport?.targetStatus, resolutionNotes)}
            disabled={updating}
            sx={{
              bgcolor: '#FE3F06',
              borderRadius: '12px',
              boxShadow: '0 4px 12px rgba(254, 63, 6, 0.3)',
              '&:hover': { bgcolor: '#e63600' },
              textTransform: 'none',
            }}
          >
            {updating ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>
      </Box>
    </Box>
  );
};

export default DefectReports;
